import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { BaseFieldFragment, JobDetailsV2Fragment } from 'graphql.generated';
import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { DEFAULT_ROOM_OPTIONS, FIELD_CONFIG } from 'src/app/global.constants';
import { YemboHelperService } from 'src/app/services/yembo-helper.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { selectQubeSheetsEnabled } from 'src/app/state/branding.store';

import { validateLocationsForEventType } from '../../job-helpers';
import { jobToolFeature, selectJob, selectJobInCurrentZone, selectIsYemboMoveCreated, selectJobLoaded, selectQubeSheetsProjectId } from '../../job-tool.reducer';
import { InventoryActions } from '../job-inventory.actions';
import { jobInventoryFeature } from '../job-inventory.feature';
import { selectInventoryTotals } from '../job-inventory.selectors';
import { InventoryItem, InventoryRoom } from '../job-inventory.state';

@Component({
    selector: 'app-inventory-table',
    standalone: true,
    imports: [
        CommonModule,
        FreyaCommonModule,
        SharedModule,
    ],
    templateUrl: './inventory-table.component.html',
    styleUrls: ['./inventory-table.component.scss'],
})
export class InventoryTableComponent {

    private _showAddRoomButtonThreshold = 15;

    public viewModel$ = this.store.select(createSelector(
        jobInventoryFeature.selectInventoryEditor,
        selectInventoryTotals,
        jobInventoryFeature.selectEditInventoryItemDialog,
        selectJob,
        selectJobLoaded,
        selectIsYemboMoveCreated,
        selectQubeSheetsProjectId,
        jobToolFeature.selectYemboSelfSurveyMutating,
        jobToolFeature.selectYemboMoveMutating,
        jobToolFeature.selectEventsMutating,
        selectJobInCurrentZone,
        selectQubeSheetsEnabled,
        (
            inventoryEditor,
            inventoryTotals,
            editInventoryItemDialog,
            job,
            jobLoaded,
            isYemboMoveCreated,
            qubeSheetsProjectId,
            isCreatingSelfSurvey,
            isStartingOnsiteEstimate,
            isBookingYemboSmartConsult,
            jobInCurrentZone,
            qubeSheetsEnabled,
        ) => {

            const {
                inventorySearch,
                inventorySearchResults,
                weightUnit,
                volumeUnit,
                addRoomDialogVisible,
                customRoomName,
                touched,
            } = inventoryEditor;

            const {
                rooms,
                totalItems,
                totalWeight,
                totalVolume,
            } = inventoryTotals;

            return {
                inventorySearch,
                inventorySearchResults,
                weightUnit,
                volumeUnit,
                rooms,
                addRoomDialogVisible,
                customRoomName,
                editItemDialogVisible: editInventoryItemDialog.selectedItem,
                editInventoryItemDialog,
                totalItems,
                totalWeight,
                totalVolume,

                focus: undefined,
                isYemboMoveCreated,
                qubeSheetsProjectId,
                isCreatingSelfSurvey,
                isStartingOnsiteEstimate,
                isBookingYemboSmartConsult,
                jobLoaded,
                hasChanges: false,
                addOtherRoomButtonDisabled: !customRoomName.trim().length,
                showAddRoomButtonAtBottom: totalItems > this._showAddRoomButtonThreshold,
                addRoomOptions: this._getAddRoomOptions(rooms, touched),
                inventoryNotes: this._getLegacyInventoryNotes(job?.fields),
                splitMenuItems: this._getSplitMenuItems(job),
                jobInCurrentZone,
                qubeSheetsEnabled,
            };
        },
    ));

    public actions = InventoryActions;

    constructor(
        public store: Store,
        public yemboHelper: YemboHelperService,
    ) { }

    private _getLegacyInventoryNotes(jobFields: Partial<BaseFieldFragment>[]) {
        const inventoryNotesField = jobFields?.find((f) => f.name === FIELD_CONFIG.jobs.inventoryNotes.name);
        return inventoryNotesField?.values[0]?.value || '' as string;
    }

    private _getAddRoomOptions(rooms: InventoryRoom[], touched: boolean) {
        const roomNames = rooms.map((r) => r.name);

        const addRoomOptions = DEFAULT_ROOM_OPTIONS.map((name) => ({
            name,
            icon: '',
            exists: roomNames.includes(name),
            focus: false,
        }));

        if (touched) {
            return addRoomOptions;
        }

        const [ firstNonExistingRoom ] = addRoomOptions.filter((opt) => !opt.exists);

        if (firstNonExistingRoom) {
            firstNonExistingRoom.focus = true;
        }

        return addRoomOptions;
    }

    private _getSplitMenuItems(job: JobDetailsV2Fragment) {
        const { disabled, disabledMessage } = this._getYemboActionsDisabled(job);

        return [
            {
                label: 'Send Yembo Self Survey',
                icon: 'pi pi-send',
                command: () => this.store.dispatch(InventoryActions.createSelfSurveyLinkMenuItemClicked()),
                disabled,
                tooltip: disabled ? disabledMessage : '',
            },
            {
                label: 'Start Yembo Onsite Estimate',
                icon: 'pi pi-play',
                command: () => this.store.dispatch(InventoryActions.startOnsiteButtonClicked()),
                disabled,
                tooltip: disabled ? disabledMessage : '',
            }
        ];
    }

    private _getYemboActionsDisabled(job: JobDetailsV2Fragment) {
        const validation = validateLocationsForEventType(job?.locations || [], 'virtualEstimate');
        return {
            disabled: !validation.isValid,
            disabledMessage: validation.errorMessage || '',
        };
    }

    public trackByRoomId(_index: number, room: InventoryRoom) {
        return room.id;
    }

    public trackByItemId(_index: number, item: InventoryItem) {
        return item.id;
    }
}
